import axios from 'axios';
import config from '@client/config';
import {
  decodeToken,
  getToken,
  replaceToken,
  isJWTExpired,
  responseFormatter,
  onTokenUpdate
} from '@client/utils';
import { noop, sessionExpireMsg } from '@client/shared/constants';

let isRefreshing = false;
let refreshSubscribers = [];
let requestWindow = 60;	
let limit = 60;	
let isFetchingJWKS = null;	
let refreshingJwks = null;

const apiInstance = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 4 * 60000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiInstance.interceptors.request.use(config => {
  if (config.setAuthHeader) {
    const token = getToken();
    config.headers.common['Authorization'] = token;
  }
  if (config.setTimeout) {
    config.timeout = config.setTimeout;
	}

  return config;
});

// Add a request interceptor
apiInstance.interceptors.request.use(
  async config => {
    // Do something before request is sent
    const originalRequest = config;
    let token = getToken();
    if (
      isJWTExpired(token) &&
      !/login/i.test(originalRequest.url) &&
      !/refreshtoken/i.test(originalRequest.url) &&
      !/profile-image/i.test(originalRequest.url) &&
			!/version/i.test(originalRequest.url)
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        const { data } = await refreshAccessToken(token);

        if (!data || !data.accessToken)
          return Promise.reject({ message: sessionExpireMsg });

        replaceToken(data.accessToken);
        isRefreshing = false;
        onRrefreshed(data.accessToken);
      }

      subscribeTokenRefresh(token => {
        // replace the expired token and retry
        originalRequest.headers['Authorization'] = token;

        return originalRequest;
      });

      return config;
    } else {
      return config;
    }
  },
  error => {
    // Do something with request error
    return Promise.reject(sessionExpireMsg);
  }
);

// apiInstance.interceptors.response.use(
//   async config => {
//     const originalRequest = config;
//     let token = getToken();
//     if (config.status === 401 && !config.hasRefreshed) {
//       if (!isRefreshing) {
//         isRefreshing = true;
//         originalRequest.hasRefreshed = true;
//         const { data } = await refreshAccessToken(token);

//         if (!data || !data.accessToken)
//           return Promise.reject({ message: sessionExpireMsg });

//         replaceToken(data.accessToken);
//         isRefreshing = false;
//         onRrefreshed(data.accessToken);
//       }

//       subscribeTokenRefresh(token => {
//         // replace the expired token and retry
//         originalRequest.headers['Authorization'] = token;
//         originalRequest.hasRefreshed = false;
//         return originalRequest;
//       });

//       return config;
//     }
//     return config;
//   },
//   error => {
//     // Do something with request error
//     return Promise.reject(sessionExpireMsg);
//   }
// );

const subscribeTokenRefresh = cb => {
  refreshSubscribers.push(cb);
};

const onRrefreshed = token => {
  refreshSubscribers.forEach(cb => {
    cb(token);
  });
  refreshSubscribers = [];
};

const refreshAccessToken = async token => {
  return await responseFormatter(
    apiInstance.get('refreshtoken', { headers: { Authorization: token } })
  );
};

let startTime = Date.now();
let remaining = 100;
let window = 60;

const updateStartTime = async (headers, startTime) => {
  let now = Date.now();
  let delay = 0;

  if (remaining == 0) {
    let diff = now - startTime;
    delay = window * 1000 - diff;
  }
  await new Promise(resolve => setTimeout(resolve, delay));
  return now + delay;
};

export const rcApi = axios.create({
  baseURL: config.rcApiBaseUrl,
  timeout: 2 * 60000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

rcApi.interceptors.request.use(async config => {
  startTime = await updateStartTime(config.headers, startTime);
  return config;
});

rcApi.interceptors.response.use(async response => {
  remaining = response.headers['x-rate-limit-remaining'];
  window = response.headers['x-rate-limit-window'];
  return response;
});

/*rcApi.interceptors.request.use(config => {
  if (config.setAuthHeader) {
    const token = getToken();
    config.headers.common['Authorization'] = token;
  }

  return config;
});*/

rcApi.interceptors.request.use(
  async config => {
    // Do something before request is sent
    const originalRequest = config;
    let token = getToken();
    if (isJWTExpired(token)) {
      subscribeTokenRefresh(token => {
        // replace the expired token and retry
        const decodedToken = decodeToken(token);
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${decodedToken.rc_access_token}`;

        if (originalRequest.headers.access_token) {
          originalRequest.headers.access_token = decodedToken.rc_access_token;
        }

        return originalRequest;
      });

      if (!isRefreshing) {
        isRefreshing = true;

        const { data } = await refreshAccessToken(token);

        if (!data || !data.accessToken)
          return Promise.reject({ message: sessionExpireMsg });

        replaceToken(data.accessToken);
        isRefreshing = false;
        onRrefreshed(data.accessToken);
      }

      return config;
    } else {
      if (!config.hasToken) {
        const decodedToken = decodeToken(getToken());
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${decodedToken.rc_access_token}`;
      }
      return config;
    }
  },
  error => {
    // Redirect user to login screen.
    return Promise.reject(sessionExpireMsg);
  }
);

// rcApi.interceptors.response.use(
//   async config => {
//     // Do something before request is sent
//     const originalRequest = config;
//     let token = getToken();
//     if (config.status === 401 && !config.hasRefreshed) {
//       subscribeTokenRefresh(token => {
//         // replace the expired token and retry
//         const decodedToken = decodeToken(token);
//         originalRequest.headers[
//           'Authorization'
//         ] = `Bearer ${decodedToken.rc_access_token}`;
//         originalRequest.hasRefreshed = false;
//         if (originalRequest.headers.access_token) {
//           originalRequest.headers.access_token = decodedToken.rc_access_token;
//         }

//         return originalRequest;
//       });

//       if (!isRefreshing) {
//         isRefreshing = true;
//         originalRequest.hasRefreshed = true;
//         const { data } = await refreshAccessToken(token);

//         if (!data || !data.accessToken)
//           return Promise.reject({ message: sessionExpireMsg });

//         replaceToken(data.accessToken);
//         isRefreshing = false;
//         onRrefreshed(data.accessToken);
//       }

//       return config;
//     } else {
//       if (!config.hasToken) {
//         const decodedToken = decodeToken(getToken());
//         originalRequest.headers[
//           'Authorization'
//         ] = `Bearer ${decodedToken.rc_access_token}`;
//       }
//       return config;
//     }
//   },
//   error => {
//     // Redirect user to login screen.
//     return Promise.reject(sessionExpireMsg);
//   }
// );

const fetchJwksToken = async (accountId, appName) => {	
  const decodedToken = decodeToken(getToken());	
  return await responseFormatter.rc(	
    rcApi.post(	
      config.rc_jwks_url,	
      {	
        appName,	
        accountId,	
      },	
      {	
        headers: {	
          access_token: decodedToken.rc_access_token,	
        },	
      }	
    )	
  );	
};

export const getAccountJwksToken = async (	
  accountUid,	
  appName = config.jwksAppName.production	
) => {	
  if (config.rcApiBaseUrl.includes('devtest')) {	
    await fetchJwksToken(accountUid, config.jwksAppName.sandBox);	
  }	
  const { data, error } = await fetchJwksToken(accountUid, appName);	
  if (data) {	
    let exp = new Date();	
    exp = exp.setSeconds(exp.getSeconds() + data.expires_in);	
    exp = Math.round(exp / 1000);	
    return {	
      data: {	
        accessToken: data.access_token,	
        exp,	
      },	
      error,	
    };	
  }	
  return { error };	
};

const validateJwks = async (	
  token,	
  accountUid,	
  appName = config.jwksAppName.production	
) => {	
  if (isJWTExpired(token, true)) {	
    refreshingJwks = refreshingJwks || getAccountJwksToken(accountUid, appName);	
    const { data, error } = await refreshingJwks;	
    if (data) {	
      return {	
        token: data,	
        hasUpdatedToken: true,	
      };	
    }	
    refreshingJwks = false;	
    return { error };	
  }	
  refreshingJwks = false;	
  return { token };	
};

export const jwksApi = axios.create({	
  baseURL: config.rcApiBaseUrl,	
  timeout: 2 * 60000,	
  headers: {	
    'Content-Type': 'application/json',	
    Accept: 'application/json',	
  },	
});	
jwksApi.interceptors.request.use(async config => {	
  startTime = await updateStartTime(config.hooks?.rateLimitReached || noop);	
  return config;	
});	
jwksApi.interceptors.response.use(async response => {	
  if (response.headers['x-rate-limit-remaining']) {	
    remaining = response.headers['x-rate-limit-remaining'];	
    // console.log("RESPONSE", response.headers["x-rate-limit-remaining"]);	
  }	
  if (response.headers['x-rate-limit-window']) {	
    requestWindow = response.headers['x-rate-limit-window'];	
  }	
  if (response.headers['x-rate-limit-limit']) {	
    limit = response.headers['x-rate-limit-limit'];	
    // console.log("LIMIT", response.headers["x-rate-limit-limit"]);	
  }	
  return response;	
});

jwksApi.interceptors.request.use(async config => {	
  const { hasToken, accountUid } = config;	
  if (hasToken) {	
    const isValidToken = await validateJwks(hasToken, accountUid);	
    if (isValidToken.error) {	
      onTokenUpdate();	
      return Promise.reject(isValidToken.error);	
    }	
    config.headers['Authorization'] = `Bearer ${isValidToken.token &&	
      isValidToken.token.accessToken}`;	
    if (isValidToken.hasUpdatedToken) {	
      config.hasUpdatedToken = true;	
      config.updatedToken = isValidToken.token;	
    }	
    return config;	
  } else {	
    const decodedToken = decodeToken(getToken());	
    config.headers['Authorization'] = `Bearer ${decodedToken.rc_access_token}`;	
  }
  return config;	
});

jwksApi.interceptors.response.use(	
  async response => {	
    if (response.config.hasUpdatedToken) {	
      response.data = {	
        ...response.data,	
        token: response.config.updatedToken,	
      };	
    }	
    return response;	
  },	
  error => {	
    if (error.config && error.config.hasToken) {	
      error.origin = 'JWKS';	
      if (error.response && error.response.status === 401) {	
        const getNewToken =	
          isFetchingJWKS || onTokenUpdate(null, error.config.accountUid);	
        isFetchingJWKS = getNewToken;	
        return (	
          getNewToken &&	
          getNewToken	
            .then(token => {	
              if (token) {	
                error.config.headers.Authorization = `Bearer ${token.accessToken}`;	
                error.config.hasToken = token;	
                setTimeout(() => (isFetchingJWKS = null), 700);	
                return jwksApi.request(error.config);	
              }	
            })	
            .catch(tokenError => {	
              Promise.reject(tokenError);	
            })	
        );	
      }	
    }	
    return Promise.reject(error);	
  }
);

const http = {
  ...apiInstance,
  rc: rcApi,
  jwks: jwksApi
};

export default http;
